import { DecimalPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";

import { catchError, finalize, of, tap } from "rxjs";

import { AuthService } from "@core/services/auth.service";
import { LoggerService } from "@core/services/logger.service";
import { ModelSelectorComponent } from "@features/documentation/components/ai-model-selector/ai-model-selector.component";
import { LoginRoutesConstants } from "@features/login/routes/login.routes.constants";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { InputComponent } from "@shared/components/input/input.component";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { ModalService } from "@shared/components/modal/modal.service";
import { TabsComponent } from "@shared/components/tabs/tabs.component";
import { Tab } from "@shared/components/tabs/tabs.model";
import { ToggleComponent } from "@shared/components/toggle/toggle.component";
import { AVATAR_UPLOAD, validateAvatarFile } from "@shared/constants/file-upload.constants";
import { User } from "@shared/models/user.model";
import { ToastService } from "@shared/services/toast.service";

@Component({
  selector: "component-user-profile-modal",
  templateUrl: "./user-profile-modal.component.html",
  styleUrl: "./user-profile-modal.component.scss",
  imports: [
    ModalComponent,
    InputComponent,
    CustomIconComponent,
    ModelSelectorComponent,
    DecimalPipe,
    TabsComponent,
    ToggleComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileModalComponent extends BaseModalComponent {
  readonly user = model.required<User>();

  private readonly _authService = inject(AuthService);
  private readonly _modalService = inject(ModalService);
  private readonly _router = inject(Router);
  private readonly _logger = inject(LoggerService);
  private readonly _toastService = inject(ToastService);

  protected readonly usage = toSignal(this._authService.getUsage());

  protected readonly isSaving = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly isHoveringAvatar = signal(false);
  protected readonly isUploadingAvatar = signal(false);

  protected readonly tabs: Tab[] = [
    { id: "profile", label: "Profile information" },
    { id: "settings", label: "Settings" }
  ];
  protected readonly activeTab = signal<Tab>(this.tabs[0]);

  protected readonly firstName = signal("");
  protected readonly lastName = signal("");
  protected readonly email = signal("");
  protected readonly defaultAiModel = signal<string | null>(null);
  protected readonly helpMeCreateAiModel = signal<string | null>(null);

  protected readonly hasAvatar = computed(() => !!this.user().avatar);
  protected readonly avatarLetter = computed(() => {
    const user = this.user();
    return user.firstName?.charAt(0)?.toUpperCase() || user.username?.charAt(0)?.toUpperCase() || "U";
  });
  protected readonly isLocalAuth = computed(() => this.user().authProvider === "LOCAL");

  constructor() {
    super();
  }

  ngOnInit(): void {
    const currentUser = this.user();

    // Initialize signals
    this.firstName.set(currentUser.firstName || "");
    this.lastName.set(currentUser.lastName || "");
    this.email.set(currentUser.email || currentUser.username || "");

    // Initialize settings from preferences
    const preferences = currentUser.preferences;
    if (preferences?.defaultAiModelId) {
      this.defaultAiModel.set(preferences.defaultAiModelId);
    }
    if (preferences?.helpMeCreateAiModelId) {
      this.helpMeCreateAiModel.set(preferences.helpMeCreateAiModelId);
    }
  }

  protected onAiModelChange(modelId: string | null): void {
    if (!modelId) return;

    this.defaultAiModel.set(modelId);
    this.updatePreferences({ defaultAiModelId: modelId });
  }

  protected onHelpMeCreateAiModelChange(modelId: string | null): void {
    if (!modelId) return;

    this.helpMeCreateAiModel.set(modelId);
    this.updatePreferences({ helpMeCreateAiModelId: modelId });
  }

  private updatePreferences(updates: Record<string, any>): void {
    // Merge with existing preferences to avoid overwriting
    const currentPrefs = this.user().preferences || {};
    const newPrefs = { ...currentPrefs, ...updates };

    this._authService
      .updatePreferences(newPrefs)
      .pipe(
        tap((updatedUser) => {
          this._toastService.success("Settings updated");
          this.user.update((u) => new User({ ...u, preferences: updatedUser.preferences }));
        }),
        catchError((error) => {
          this._logger.error("Failed to update settings", error);
          this._toastService.error("Failed to update settings");
          return of(null);
        })
      )
      .subscribe();
  }

  protected onTabChange(tab: Tab): void {
    this.activeTab.set(tab);
  }

  protected onAvatarClick(): void {
    if (!this.isUploadingAvatar()) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = AVATAR_UPLOAD.ACCEPT_STRING;
      input.onchange = (e: any): void => {
        const file = (e.target as HTMLInputElement)?.files?.[0];
        if (file) {
          const validationError = validateAvatarFile(file);
          if (validationError) {
            this.errorMessage.set(validationError);
            return;
          }
          this.uploadAvatar(file);
        }
      };
      input.click();
    }
  }

  protected onDeleteAvatar(): void {
    this.isUploadingAvatar.set(true);
    this._authService
      .deleteAvatar()
      .pipe(
        tap((updatedUser) => {
          this._toastService.success("Avatar deleted successfully");
          // Update only the avatar URL to preserve other unsaved form changes
          this.user.update((u) => new User({ ...u, avatarUrl: updatedUser.avatarUrl }));
        }),
        catchError((error) => {
          this._logger.error("Failed to delete avatar", error);
          this.errorMessage.set("Failed to delete avatar. Please try again.");
          return of(null);
        }),
        finalize(() => {
          this.isUploadingAvatar.set(false);
        })
      )
      .subscribe();
  }

  private uploadAvatar(file: File): void {
    this.isUploadingAvatar.set(true);
    this.errorMessage.set(null);

    this._authService
      .uploadAvatar(file)
      .pipe(
        tap((updatedUser) => {
          this._toastService.success("Avatar uploaded successfully");
          // Update only the avatar URL to preserve other unsaved form changes
          this.user.update((u) => new User({ ...u, avatarUrl: updatedUser.avatarUrl }));
        }),
        catchError((error) => {
          this._toastService.error("Failed to upload avatar", error);
          this.errorMessage.set("Failed to upload avatar. Please try again.");
          return of(null);
        }),
        finalize(() => {
          this.isUploadingAvatar.set(false);
        })
      )
      .subscribe();
  }

  protected onChangePassword(): void {
    if (!this.isLocalAuth()) {
      return;
    }
    this.activeModal.close();
    // Pass email and context flag to password reset modal
    this._modalService.openPasswordResetModal(this.email(), true);
  }

  protected onDeleteAccount(): void {
    if (this._authService.currentUser()?.isTemporary) {
      return;
    }
    this._modalService.confirm("Delete Account", "This action cannot be undone.").then((confirmed) => {
      if (confirmed) {
        this._authService.deleteAccount().subscribe({
          next: () => {
            this._router.navigate([LoginRoutesConstants.main()]).then();
          }
        });
      }
    });
  }

  protected onSave(): void {
    if (this.isSaving()) {
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set(null);

    // Only profile tab has manual save
    this._authService
      .updateProfile(this.firstName(), this.lastName())
      .pipe(
        tap((updatedUser) => {
          this._toastService.success("Profile updated successfully");
          // Do not update the global user signal directly here to avoid resetting stores
          // Instead we just return the updated user to the caller
          this.activeModal.close(updatedUser);
        }),
        catchError((error) => {
          this._toastService.error("Failed to update profile", error);
          this.errorMessage.set("Failed to update profile. Please try again.");
          return of(null);
        }),
        finalize(() => {
          this.isSaving.set(false);
        })
      )
      .subscribe();
  }
}
